import CircleXMark from "@dana/Icons/CircleXMark";
import DownloadIcon from "@dana/Icons/DownloadIcon";
import MessageIcon from "@dana/Icons/MessageIcon";
import PlusIcon from "@dana/Icons/PlusIcon";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import PostLiteraturePdfEmailHandler from "@dana/Store/Data/Email/Handlers/PostLiteraturePdfEmailHandler";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { DocumentModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps, IconWrapper } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import useIsEmea from "@dana/Common/Hooks/useIsEmea";

const mapStateToProps = (state: ApplicationState) => ({
    danaSettings : getDanaGeneralSettings(state),
    isNARegion: useIsNA(state),
    isEMEARegion: useIsEmea(state),
});

const mapDispatchToProps = {
    PostLiteraturePdfEmailHandler,
};
type OwnProps = HasProduct & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsLiteratureDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    literatureCard?: InjectableCss;
    documentActions?: InjectableCss;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    shareButton?: LinkPresentationProps;
    messageIcon?: IconProps;
    recepientText?: TypographyPresentationProps;
    emailFieldWrapper?: InjectableCss;
    addEmailButton?: ButtonPresentationProps;
    removeEmailButton?: ButtonPresentationProps;
    documentNameText?: TypographyPresentationProps;
    documentDescTextWrapper?: InjectableCss;
    documentDescText?: TypographyPresentationProps;
}

export const literaturedocumentsStyles: ProductDetailsLiteratureDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
            @media print {
                padding: 0 0 16px;
            }
        `,
    },
    documentLink: {
        css: css`
            @media print {
                display: none;
            }
        `
    },
    container: {
        gap: 16,
    },
    documentGridItem: {
        width: [6, 4, 3, 2, 2],
        css: css`
            flex-direction: column;
            row-gap: 8px;
        `,
    },
    literatureCard: {
        css: css`
            width: 100%;
            max-width: 145px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin: 0 auto;
        `,
    },
    documentActions: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            min-height: 188px;
            display: flex;
            max-width: 100%;
            align-items: center;
            justify-content: center;
            max-height: 188px;
            width: 100%;
            position: relative;
            @media print {
                min-height: 80px;
                max-height: 80px;
            }
        `
    },
    img: {
        css: css`
            width: 100%;
            display: block;
            position: absolute;
            top: auto;
            max-height: 180px;
            object-fit: contain;
            @media print {
                max-height: 80px;
            }
        `,
    },
    documentIcon: {
        src: DownloadIcon,
        color: themeColors.primary.danaBlue,
        css: css`
            &:hover {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }
        `,
    },
    shareButton: {
        css: css``,
    },
    recepientText: {
        size: fontSizes.medium,
        color: themeBackgrounds.darkGray,
        css: css`
            display: block;
            margin-bottom: 8px;
        `,
    },
    emailFieldWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 16px;

            &:first-of-type {
                .remove-button {
                    visibility: hidden;
                }
            }
        `,
    },
    messageIcon: {
        color: themeColors.primary.danaBlue,
        css: css`
            &:hover {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }
        `,
    },
    addEmailButton: {
        css: css`
            width: 30px;
            height: 30px;
            min-height: 30px;
            border-radius: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 10px;
            margin: 8px 0;

            ${IconWrapper} {
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        `,
    },
    removeEmailButton: {
        css: css`
            background: transparent;
            padding: 0;
            border: 0;
            min-height: auto;
            height: 30px;
            width: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${themeBackgrounds.lightGray};
            border-radius: 50%;
            flex-shrink: 0;

            ${IconWrapper} {
                width: 16px;
                height: 16px;
                svg {
                    width: 16px;
                    height: 16px;
                    path {
                        fill: ${themeColors.primary.richBlack};
                    }
                }
            }
        `,
    },
    documentDescTextWrapper: {
        css: css`
            position: relative;
        `,
    },
    documentDescText: {
        size: fontSizes.small,
        color: themeColors.primary.danaBlue,
        weight: fontWeights.semiBold,
        css: css`
            vertical-align: super;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: default;

            &:hover {
                & + .doc-tooltip {
                    visibility: visible;
                }
            }
            @media print {
                font-size: ${fontSizes.xSmall};
            }
        `,
    },
    documentNameText: {
        size: fontSizes.xSmall,
        color: themeBackgrounds.darkGray,
        css: css`
            vertical-align: super;
        `,
    },
};

const TooltipStyle = styled.div`
    visibility: hidden;
    background-color: ${themeColors.primary.white};
    color: ${themeColors.primary.richBlack};
    text-align: center;
    padding: 10px;
    position: absolute;
    z-index: 1000;
    top: 100%;
    left: 0;
    white-space: normal;
    width: max-content;
    box-shadow: ${elevation.hover};
    font-size: ${fontSizes.small};
    line-height: ${lineHeights.default};
`;

const styles = literaturedocumentsStyles;

const MAX_EMAIL_FIELDS = 5;

const ProductDetailsLiteratureDocs = ({ product, PostLiteraturePdfEmailHandler, danaSettings, isNARegion, isEMEARegion }: OwnProps) => {
    const [selectedDocument, setSelectedDocument] = useState<DocumentModel | null>(null);
    const toasterContext = React.useContext(ToasterContext);
    if (!product.documents || product.documents.length === 0) {
        return null;
    }
    const documents = product.documents;
    const [availabilityModalIsOpen, setAvailabilityModalIsOpen] = useState<boolean>(false);
    const defaultThumbnailImage = danaSettings?.defaultLiteratureImage;

    const getFileNameWithoutExtension = (url: string) => {
        if (!url) {
            return "";
        }
        const parts = url.split("/");
        const lastPart = parts.pop() || "";
        const fileNameWithParams = lastPart.split("?")[0].split("#")[0];
        const fileNameWithoutExt = fileNameWithParams.replace(/\.[^/.]+$/, ""); // remove extension
        return decodeURIComponent(fileNameWithoutExt);
    };

    const modalCloseHandler = () => {
        setAvailabilityModalIsOpen(false);
    };

    const [emails, setEmails] = useState<string[]>([""]);
    const [emailErrors, setEmailErrors] = useState<string[]>(Array(emails.length).fill(""));
    const [recipients, setRecipients] = React.useState<string[]>([]);

    const validateEmail = (email: string): string => {
        if (!email.trim()) {
            return translate("Email is required");
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return translate("Invalid email format");
        }
        return "";
    };

    // Handler for adding a new email field
    const handleAddEmail = () => {
        if (emails.length < MAX_EMAIL_FIELDS) {
            setEmails(prevEmails => [...prevEmails, ""]);
            setEmailErrors(prevErrors => [...prevErrors, ""]); // Add error slot for new field
        } else {
            // eslint-disable-next-line no-alert
            alert(translate(`You can add a maximum of ${MAX_EMAIL_FIELDS} email fields.`));
        }
    };

    // Handler for removing an email field by index
    const handleRemoveEmail = (indexToRemove: number) => {
        if (emails.length > 1) {
            // Ensure at least one field remains
            setEmails(prevEmails => prevEmails.filter((_, index) => index !== indexToRemove));
            setEmailErrors(prevErrors => prevErrors.filter((_, index) => index !== indexToRemove));
        } else {
            // Optionally, prevent removing the last field or clear its value
            setEmails([""]); // Clear the last field if only one remains
            setEmailErrors([""]); // Clear its error
        }
    };

    // Handler for changing an individual email's value
    const handleEmailChange = (index: number, value: string) => {
        const newEmails = [...emails];
        newEmails[index] = value;
        setEmails(newEmails);

        // Re-validate the specific field on change
        const newErrors = [...emailErrors];
        newErrors[index] = validateEmail(value);
        setEmailErrors(newErrors);
    };

    // --- Validation for all emails (e.g., on form submission) ---
    const validateAllEmails = () => {
        let allValid = true;
        const newErrors = emails.map((email, index) => {
            const error = validateEmail(email);
            if (error) {
                allValid = false;
            }
            return error;
        });
        setEmailErrors(newErrors);
        return allValid;
    };

    const handleSendEmail = (e: any, product: ProductModel) => {
        if (validateAllEmails()) {
            PostLiteraturePdfEmailHandler({
                recipientEmailAddress: emails.join(","),
                productId: product.id,
                documentId: selectedDocument?.id,
                pdfUrl: selectedDocument?.filePath,
                pdfName: selectedDocument?.name,
                productTitle: product.productTitle,
                productNumber: product.productNumber,
                productBrand: product.brand?.name,
                productUrl: window.location.href,
                onSuccess: () => {
                    setAvailabilityModalIsOpen(false);
                    setEmails([""]);
                    toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                    }
                },
                onError: (errorResponse: any) => {
                    // Handle error response
                    // eslint-disable-next-line no-console
                    console.error("Error Response", errorResponse);
                    toasterContext.addToast({
                        body: translate("Error Occured, Please try after some time"),
                        messageType: "danger",
                    });
                    setAvailabilityModalIsOpen(false);
                },
            });
        } else {
            translate("Validation failed. Please check errors.");
        }

        setEmails([""]);
    };

    
    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                {documents
                    .filter(document => document.documentType?.toLowerCase() === "pdf")
                    .map((document) => {
                        const thumbnail = product.properties["docThumbnail_" + document.id];
                        const thumbnailImageGyor = `/userfiles/gyorpartsliteraturethumbnails/${document.name}.jpg`;
 
                        return (
                            <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                <StyledWrapper {...styles.literatureCard}>
                                    {isEMEARegion ? 
                                        <>
                                            {thumbnailImageGyor && thumbnailImageGyor !== '' &&
                                                <StyledWrapper {...styles.imageWrapper}>
                                                    <Img {...styles.img} src={thumbnailImageGyor} />
                                                </StyledWrapper>
                                            }
                                        </> :
                                        <>
                                            {thumbnail && thumbnail !== '' &&
                                                <StyledWrapper {...styles.imageWrapper}>
                                                    <Img {...styles.img} src={thumbnail} />
                                                </StyledWrapper>
                                            }
                                        </>
                                    }
                                    {isNARegion && !thumbnail && 
                                        <StyledWrapper {...styles.imageWrapper}>
                                            <Img {...styles.img} src={defaultThumbnailImage} />
                                        </StyledWrapper>
                                    }
                                    <StyledWrapper {...styles.documentActions}>
                                        <Link
                                            href={document.filePath}
                                            target="_new"
                                            {...styles.documentLink}
                                            data-test-selector={`productDetails_${document.id}`}
                                        >
                                            <IconMemo size={16} {...styles.documentIcon} />
                                        </Link>
                                        <Link
                                            onClick={() => {
                                                setSelectedDocument(document);
                                                setAvailabilityModalIsOpen(true);
                                            }}
                                            data-test-selector="share_literature_button"
                                            {...styles.shareButton}
                                        >
                                            <IconMemo size={16} src={MessageIcon} {...styles.messageIcon} />
                                        </Link>
                                    </StyledWrapper>
                                    {document?.description && (
                                        <StyledWrapper {...styles.documentDescTextWrapper}>
                                            <Typography {...styles.documentDescText}>{document.description}</Typography>
                                            <TooltipStyle className="doc-tooltip">{document?.description}</TooltipStyle>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        )
                    })
                }
            </GridContainer>
            <Modal headline={translate("Share")} isOpen={availabilityModalIsOpen} handleClose={modalCloseHandler}>
                <Typography {...styles.recepientText}>{translate("Recepients")}</Typography>
                {emails.map((email, index) => (
                    <StyledWrapper key={index} {...styles.emailFieldWrapper}>
                        {" "}
                        {/* Using index as key for simplicity here, but a unique ID is better if emails can be reordered */}
                        <TextField
                            name={`email-${index}`}
                            id={`email-${index}`}
                            autoComplete={`section-recipient-${index} email`}
                            value={email}
                            onChange={e => handleEmailChange(index, e.target.value)}
                            data-test-selector={`recepientEmail-${index}`}
                            error={emailErrors[index]}
                        />
                        {/* Only show close button if there's more than one field OR it's not the first field and the list is > 1 */}
                        {emails.length > 1 && (
                            <Button
                                onClick={() => handleRemoveEmail(index)}
                                aria-label={translate("Remove email field")}
                                {...styles.removeEmailButton}
                                className="remove-button"
                            >
                                <IconMemo size={30} src={CircleXMark} />
                            </Button>
                        )}
                    </StyledWrapper>
                ))}

                {/* Plus button to add new fields, limited to MAX_EMAIL_FIELDS */}
                {emails.length < MAX_EMAIL_FIELDS && (
                    <Button
                        onClick={handleAddEmail}
                        aria-label={translate("Add new email field")}
                        {...styles.addEmailButton}
                    >
                        <IconMemo size={16} src={PlusIcon} />
                    </Button>
                )}

                <Button onClick={event => handleSendEmail(event, product as ProductModel)}>
                    {translate("SEND EMAIL")}
                </Button>
            </Modal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsLiteratureDocs));
